import React, { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Dialog } from 'primereact/dialog';

import { ModalHeader } from 'shared/ModalHeader';
import { useAccounts } from 'hooks/useAccounts';
import TemporaryFundHeaderV2 from './facets/TemporaryFundHeaderV2';
import TemporaryFundCaseNameV2 from './facets/TemporaryFundCaseNameV2';
import TemporaryFundTrustAccountV2 from './facets/TemporaryFundTrustAccountV2';
import TemporaryFundContactV2 from './facets/TemporaryFundContactV2';
import TemporaryFundAmountV2 from './facets/TemporaryFundAmountV2';
import TemporaryFundDateV2 from './facets/TemporaryFundCreateDateV2';
import TemporaryFundDescriptionV2 from './TemporaryFundDescriptionV2';
import TemporaryFundPaymentMethodV2 from './facets/TemporaryFundPaymentMethodV2';
import TemporaryFundChequeNumberV2 from './facets/TemporaryFundChequeNumberV2';
import TemporaryFundFormButtonV2 from './TemporaryFundFormButtonV2';
import { generateFullName } from 'utils/generateFullNameUtils';
import { temporaryFundDefaulftFormValues } from '../../constants/temporaryFundConfigsV2';
import { getCaseDetails } from 'services/CaseServices';
import Cookies from 'universal-cookie';
const cookie = new Cookies();

const WithdrawOrDepositTemporaryFundV2 = (props) => {
  const {
    onHide,
    caseDetails,
    temporaryFundModalOptions,
    depositOrWithdrawTemporaryFundAmount,
    updateTemporaryFundTransactions,
    temporaryFundBalance,
  } = props;
  const case_id = caseDetails?.case_id || temporaryFundModalOptions?.selectedRowData?.case_id;
  const case_name = caseDetails?.case_description?.case_name || temporaryFundModalOptions?.selectedRowData?.case_name;
  const caseName = case_name || '';

  const [defaultTrustAccountDetails, setDefaultTrustAccountDetails] = useState(
    caseDetails?.default_trust_account?.length ? caseDetails.default_trust_account[0] : null
  );
  const modalType = temporaryFundModalOptions?.entryType === 'db' ? 'Withdraw' : 'Deposit';
  const shouldCallListAccountApi = useMemo(() => {
    return !defaultTrustAccountDetails?.account_name;
  }, [defaultTrustAccountDetails]);

  const isUpdateScreen = temporaryFundModalOptions?.isUpdate;

  const { accountList: trustAccountsList = [] } = useAccounts({ accountType: 'trust', shouldCallListAccountApi });

  const [toggleAddNewContact, setToggleAddNewContact] = useState(false);
  const [caseContactsList, setCaseContactsList] = useState([]);

  // react-hook form validation
  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({ defaultValues: temporaryFundDefaulftFormValues });

  // Watch payment method to conditionally show trust account and filter contacts
  const paymentMethod = useWatch({
    control,
    name: 'paymentMethod',
  });

  // Clear contact when Payment or Transfer changes (To Client vs To Third Party have different contact types)
  useEffect(() => {
    if (modalType === 'Withdraw' && (paymentMethod === 'to_client' || paymentMethod === 'to_third_party')) {
      setValue('fromOrToContact', null);
    }
  }, [paymentMethod, modalType, setValue]);

  const handleHideModal = () => {
    onHide();
    reset();
  };

  // Store running trust balance when fetched (only needed when caseDetails is not available)
  const [fetchedRunningTrustBalance, setFetchedRunningTrustBalance] = useState(null);

  // Get running trust balance from caseDetails (prop) or fetched data
  const runningTrustBalance = caseDetails?.waiting_payments?.running_trust_balance || fetchedRunningTrustBalance;

  const onSubmit = async (formValues) => {
    try {
      let palyloadData = {};

      palyloadData['case_id'] = case_id;
      palyloadData['case_name'] = case_name;
      palyloadData['entry_type'] = temporaryFundModalOptions?.entryType;
      palyloadData['operation'] = temporaryFundModalOptions?.entryType === 'cr' ? 'deposit' : 'withdrawal';
      palyloadData['amount'] = formValues?.amount?.toString();
      palyloadData['description'] = formValues?.description;
      // TimezoneDatePicker already returns an ISO string with correct timezone
      palyloadData['record_created_date'] = formValues?.recordCreateDate;
      palyloadData['deposit_into'] = '';
      palyloadData['payment_method'] = formValues?.paymentMethod;

      palyloadData['account_name'] = defaultTrustAccountDetails?.account_name || formValues?.trustAccount?.account_name;
      palyloadData['account_id'] = defaultTrustAccountDetails?.account_id || formValues?.trustAccount?.account_id;

      // Cheque: deposit cheque, withdraw to client, or withdraw to third party
      if (
        formValues?.paymentMethod === 'cheque_payment' ||
        formValues?.paymentMethod === 'to_client' ||
        formValues?.paymentMethod === 'to_third_party'
      ) {
        palyloadData['cheque_number'] = formValues?.chequeNumber || '';
      }

      // Contact: deposit, withdraw to client, or withdraw to third party
      if (
        temporaryFundModalOptions?.entryType === 'cr' ||
        formValues?.paymentMethod === 'to_client' ||
        formValues?.paymentMethod === 'to_third_party'
      ) {
        const contactDetails =
          temporaryFundModalOptions?.entryType === 'cr' ? { fullName: formValues?.fromOrToContact } : formValues?.fromOrToContact;
        palyloadData['contact_details'] = contactDetails;
      }

      const apiFunctionParams = {
        data: palyloadData,
        hideModal: handleHideModal,
      };

      if (!isUpdateScreen) {
        await depositOrWithdrawTemporaryFundAmount(apiFunctionParams);
      } else {
        apiFunctionParams['data']['pk'] = temporaryFundModalOptions?.selectedRowData?.pk;
        apiFunctionParams['data']['sk'] = temporaryFundModalOptions?.selectedRowData?.sk;
        await updateTemporaryFundTransactions(apiFunctionParams);
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const getCaseContactsList = () => {
    let allCaseContacts = [...caseDetails?.client_details, ...caseDetails?.relations];
    let formattedCaseContacts = allCaseContacts?.map((contact) => {
      return {
        ...contact,
        fullName: generateFullName(contact),
      };
    });

    const sortedContacts = formattedCaseContacts?.sort((a, b) => {
      const nameA = a?.first_name || a?.title || '';
      const nameB = b?.first_name || b?.title || '';
      return nameA.toLowerCase().localeCompare(nameB.toLowerCase());
    });
    setCaseContactsList(sortedContacts);
  };

  const fetchCaseData = async () => {
    let tenantId = cookie.get('tenantId') || null;
    let tenant_sk = cookie.get('tenantSk') || null;
    let tenantSk = tenant_sk ? tenant_sk.replace('#', '%23') : '';
    let caseSkModified = temporaryFundModalOptions?.selectedRowData?.sk.replace('#', '%23').replace('#', '%23');
    let pkModified = temporaryFundModalOptions?.selectedRowData?.pk.replace('#', '%23');
    try {
      const response = await getCaseDetails(tenantId, tenantSk, pkModified, caseSkModified, {}, case_id);
      if (response?.data) {
        const caseData = response?.data;
        let allCaseContacts = [...caseData?.client_details, ...caseData?.relations];

        const formattedCaseContacts = allCaseContacts?.map((contact) => ({
          ...contact,
          fullName: generateFullName(contact),
        }));

        // Sort all contacts alphabetically
        const sortedContacts = formattedCaseContacts?.sort((a, b) => {
          const nameA = a?.first_name || a?.title || '';
          const nameB = b?.first_name || b?.title || '';
          return nameA.toLowerCase().localeCompare(nameB.toLowerCase());
        });

        setCaseContactsList(sortedContacts);
        setDefaultTrustAccountDetails(caseData?.default_trust_account?.length ? caseData?.default_trust_account[0] : null);
        setFetchedRunningTrustBalance(caseData?.waiting_payments?.running_trust_balance || null);
      }
    } catch (error) {
      console.error('error', error);
    }
  };

  useEffect(() => {
    if (caseDetails) {
      getCaseContactsList();
    } else if (isUpdateScreen && temporaryFundModalOptions?.selectedRowData?.case_id) {
      fetchCaseData();
    }
  }, [caseDetails, isUpdateScreen, temporaryFundModalOptions?.selectedRowData?.case_id]);

  // update temporay fund data
  useEffect(() => {
    if (isUpdateScreen) {
      const {
        entry_type,
        account_name,
        account_id,
        contact_details,
        amount,
        record_created_date,
        description,
        payment_method,
        cheque_number,
      } = temporaryFundModalOptions?.selectedRowData;
      /** --- Setting multiple form values at a time --- **/
      reset((formValues) => {
        return {
          ...formValues,
          trustAccount: {
            account_type: 'trust',
            account_name,
            account_id,
          },
          fromOrToContact: entry_type === 'cr' ? contact_details?.fullName : { ...contact_details },
          recordCreateDate: record_created_date || '',
          amount,
          description,
          paymentMethod: payment_method || '',
          chequeNumber: cheque_number || '',
        };
      });
    }
  }, [temporaryFundModalOptions]);

  return (
    <Dialog
      visible
      closable={false}
      onHide={onHide}
      header={ModalHeader}
      className="create-msg-popup" //className should be checked and changed
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="row">
          {/* Modal heading */}
          <TemporaryFundHeaderV2 modalType={modalType} />

          {/* Case name */}
          <TemporaryFundCaseNameV2 caseName={caseName} />

          {/* Payment or Transfer (withdraw); Payment Method (deposit) */}
          <TemporaryFundPaymentMethodV2 control={control} errors={errors} isWithdraw={modalType === 'Withdraw'} />

          {/* Trust account - show for Deposit: Transfer from Trust Account, or Withdraw: Designate Back to Trust, or Cheque Payment without default account */}
          {(paymentMethod === 'transfer_from_trust_account' ||
            paymentMethod === 'transfer_in_trust_account' ||
            (paymentMethod === 'cheque_payment' && !defaultTrustAccountDetails)) && (
            <TemporaryFundTrustAccountV2
              defaultTrustAccountDetails={defaultTrustAccountDetails}
              trustAccountsList={trustAccountsList}
              control={control}
              errors={errors}
              paymentMethod={paymentMethod}
            />
          )}

          {/* Cheque Number - Withdraw: To Client / To Third Party */}
          {(paymentMethod === 'cheque_payment' || paymentMethod === 'to_client' || paymentMethod === 'to_third_party') && (
            <TemporaryFundChequeNumberV2 control={control} errors={errors} paymentMethod={paymentMethod} />
          )}

          {/* Payable To - Withdraw: To Client or To Third Party */}
          {(modalType === 'Deposit' || paymentMethod === 'to_client' || paymentMethod === 'to_third_party') && (
            <TemporaryFundContactV2
              control={control}
              errors={errors}
              temporaryFundModalOptions={temporaryFundModalOptions}
              toggleAddNewContact={toggleAddNewContact}
              setToggleAddNewContact={setToggleAddNewContact}
              caseContactsList={caseContactsList}
              paymentMethod={paymentMethod}
              modalType={modalType}
            />
          )}

          {/* Amount */}
          <TemporaryFundAmountV2
            control={control}
            errors={errors}
            temporaryFundBalance={temporaryFundBalance}
            isWithdraw={modalType === 'Withdraw'}
            runningTrustBalance={runningTrustBalance}
            paymentMethod={paymentMethod}
          />

          {/* Date */}
          <TemporaryFundDateV2 control={control} errors={errors} />

          {/* Description */}
          <TemporaryFundDescriptionV2 control={control} errors={errors} />
        </div>

        <hr></hr>

        {/* Button section */}
        <TemporaryFundFormButtonV2
          loading={isSubmitting}
          handleHideModal={handleHideModal}
          modalType={modalType}
          isUpdateScreen={isUpdateScreen}
        />
      </form>
    </Dialog>
  );
};

export default WithdrawOrDepositTemporaryFundV2;
